// src/components/FreeFlow.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Play, Square, UserPlus, Plus, RotateCcw, Trash2, Check, X, ChevronLeft, Clock, Edit, Lock, Unlock } from "lucide-react";
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  onSnapshot,
  query,
  where,
  arrayUnion,
  serverTimestamp,
  getDoc,
  getDocs,
  deleteDoc,
  arrayRemove,
  setDoc,
  deleteField,
  limit,
  enableIndexedDbPersistence,
  CACHE_SIZE_UNLIMITED,
  writeBatch
} from "firebase/firestore";
import { db, auth } from "../firebase";
import flowFrameLogo from "../data/FlowFrame Logo.png";

console.log("Loading FreeFlow component with subcollection-based markers - Version 1.0.3");

// Helper function to format milliseconds to MM:SS
const formatTime = (ms) => {
  // Handle negative times for count-in display
  const isNegative = ms < 0;
  const absMs = Math.abs(ms);
  
  const totalSeconds = Math.floor(absMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  
  // Format the time with a minus sign for count-in
  return `${isNegative ? '-' : ''}${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Helper function to parse time strings like "1:30" or "90" to milliseconds
const parseTimeToMs = (timeStr) => {
  if (!timeStr) return null;
  
  // If it's just a number (seconds)
  if (/^\d+$/.test(timeStr)) {
    return parseInt(timeStr, 10) * 1000;
  }
  
  // If it's in MM:SS format
  const match = timeStr.match(/^(\d+):(\d+)$/);
  if (match) {
    const minutes = parseInt(match[1], 10);
    const seconds = parseInt(match[2], 10);
    return (minutes * 60 + seconds) * 1000;
  }
  
  return null;
};

// Format date to a user-friendly string
const formatDate = (timestamp) => {
  if (!timestamp) return '';
  
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  
  // Check if it's today
  const today = new Date();
  if (date.toDateString() === today.toDateString()) {
    return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }
  
  // Check if it's yesterday
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }
  
  // Otherwise return the full date
  return date.toLocaleString([], { 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit', 
    minute: '2-digit'
  });
};

export default function FreeFlow({ currentUser, subscriptionTier }) {
  // Basic error handling for component-level errors
  const [componentError, setComponentError] = useState(null);
  
  // Debug logging to track component loading
  useEffect(() => {
    try {
      console.log("FreeFlow component mounted", { 
        hasCurrentUser: !!currentUser,
        userEmail: currentUser?.email,
        subscriptionTier
      });
      
      // Check Firestore permissions
      if (currentUser) {
        const checkFirestorePermissions = async () => {
          try {
            // Try to read from collections that should be accessible
            // First check freeflows where user is a participant
            const flowsQuery = query(
              collection(db, "freeflows"), 
              where("participants", "array-contains", currentUser.uid),
              limit(1)
            );
            
            try {
              await getDocs(flowsQuery);
              console.log("Firestore permissions check: Access to freeflows granted");
            } catch (flowsError) {
              console.warn("Freeflows permission check failed:", flowsError.message);
              // Don't set error here as it's not critical - will be fixed by the new rules
            }
            
            // Check invitations access
            try {
              const invitationsQuery = query(
                collection(db, "invitations"),
                where("recipientEmail", "==", currentUser.email),
                limit(1)
              );
              await getDocs(invitationsQuery);
              console.log("Firestore permissions check: Access to invitations granted");
            } catch (invitationsError) {
              console.warn("Invitations permission check failed:", invitationsError.message);
              // Don't set error - will be fixed by the new rules
            }
          } catch (error) {
            console.error("Firestore general permissions check failed:", error);
            // Only set error if it's not a permission error (which will be fixed by rule changes)
            if (error.code !== 'permission-denied') {
              setError("Firestore access error: " + error.message);
            }
          }
        };
        
        checkFirestorePermissions().catch(e => {
          console.error("Permission check failed:", e);
          // Only set error for non-permission issues (connection problems, etc.)
          if (e.code !== 'permission-denied') {
            setError("Failed to connect to Firestore: " + e.message);
          }
        });
      }
      
      return () => {
        console.log("FreeFlow component unmounting");
      };
    } catch (error) {
      console.error("Error in mount effect:", error);
      setComponentError(error);
    }
  }, [currentUser, subscriptionTier]);
  
  // Session state
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  
  // Timer state
  const [timerRunning, setTimerRunning] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef(null);
  const lastUpdateTimeRef = useRef(null);
  
  // Markers state
  const [markers, setMarkers] = useState([]);
  const [selectedMarkers, setSelectedMarkers] = useState([]);
  const [showAddMarker, setShowAddMarker] = useState(false);
  const [newMarkerName, setNewMarkerName] = useState("");
  const [newMarkerTime, setNewMarkerTime] = useState("");
  const [newMarkerCountdown, setNewMarkerCountdown] = useState("");
  
  // Edit marker state
  const [showEditMarker, setShowEditMarker] = useState(false);
  const [editingMarker, setEditingMarker] = useState(null);
  const [editMarkerName, setEditMarkerName] = useState("");
  const [editMarkerTime, setEditMarkerTime] = useState("");
  const [editMarkerCountdown, setEditMarkerCountdown] = useState("");
  
  // Personalized marker settings for participants
  const [personalizedMarkers, setPersonalizedMarkers] = useState({});
  const [showPersonalizeMarker, setShowPersonalizeMarker] = useState(false);
  const [personalizingMarker, setPersonalizingMarker] = useState(null);
  const [personalizeMarkerName, setPersonalizeMarkerName] = useState("");
  const [personalizeMarkerCountdown, setPersonalizeMarkerCountdown] = useState("");
  
  // Create session state
  const [showCreateSession, setShowCreateSession] = useState(false);
  const [newSessionName, setNewSessionName] = useState("");
  const [countInDuration, setCountInDuration] = useState(5);
  
  // Invite state
  const [inviteEmail, setInviteEmail] = useState("");
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [showInvitations, setShowInvitations] = useState(false);
  
  // Custom time state
  const [customStartTime, setCustomStartTime] = useState("");
  
  // Error state
  const [error, setError] = useState(null);
  const [showInvitationErrors, setShowInvitationErrors] = useState(false);
  
  // Controls lock state
  const [isControlsLocked, setIsControlsLocked] = useState(false);
  
  // Screen lock state
  const [isScreenLocked, setIsScreenLocked] = useState(false);
  const [lastTapTime, setLastTapTime] = useState(0);
  const doubleTapTimeoutRef = useRef(null);
  
  // Global timer notification state for participants
  const [showGlobalTimerNotification, setShowGlobalTimerNotification] = useState(null);
  
  // Cleanup function for listeners
  const unsubscribes = useRef([]);
  const cleanupListeners = useCallback(() => {
    unsubscribes.current.forEach(unsubscribe => {
      if (typeof unsubscribe === 'function') {
        try {
          unsubscribe();
        } catch (error) {
          console.warn("Error unsubscribing:", error);
        }
      }
    });
    unsubscribes.current = [];
  }, []);

  // Clean up all listeners when unmounting
  useEffect(() => {
    return cleanupListeners;
  }, [cleanupListeners]);

  // Load user's sessions
  useEffect(() => {
    if (!currentUser) return;
    
    try {
      const q = query(
        collection(db, "freeflows"), 
        where("participants", "array-contains", currentUser.uid)
      );
      
      const unsubscribe = onSnapshot(q, 
        (snapshot) => {
          const sessionData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setSessions(sessionData);
          setError(null);
        },
        (error) => {
          console.error("Error loading sessions:", error);
          setError("Failed to load your sessions. Please try again later.");
        }
      );
      
      unsubscribes.current.push(unsubscribe);
      
      return () => {
        unsubscribe();
        unsubscribes.current = unsubscribes.current.filter(fn => fn !== unsubscribe);
      };
    } catch (error) {
      console.error("Error setting up sessions listener:", error);
      setError("Failed to connect to the database. Please try again later.");
    }
  }, [currentUser]);
  
  // Load pending invitations
  useEffect(() => {
    if (!currentUser) return;
    
    try {
      const q = query(
        collection(db, "invitations"),
        where("recipientEmail", "==", currentUser.email),
        where("status", "==", "pending")
      );
      
      const unsubscribe = onSnapshot(q, 
        (snapshot) => {
          const inviteData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setPendingInvitations(inviteData);
          
          // Automatically show invitations panel if there are new invites
          if (inviteData.length > 0 && !showInvitations && !activeSession) {
            setShowInvitations(true);
          }
           
          // Clear any previous invitation-related error
          if (error && error.includes("invitations")) {
            setError(null);
          }
        },
        (error) => {
          // Only log the error but don't show it to the user unless they explicitly
          // interact with invitation-related features
          console.error("Error loading invitations:", error);
          
          // For permission errors, we'll just silently fail and not show errors to the user
          // This prevents the continuous error messages in the console
          if (error.code === 'permission-denied') {
            console.warn("Permission denied for invitations. This is expected if the rules were recently updated.");
            // Return empty invitations instead of failing
            setPendingInvitations([]);
          } else if (showInvitationErrors) {
            // Only show other types of errors if user is actively using invitation features
            setError("Failed to load your invitations. Please try again later.");
          }
        }
      );
      
      unsubscribes.current.push(unsubscribe);
      
      return () => {
        unsubscribe();
        unsubscribes.current = unsubscribes.current.filter(fn => fn !== unsubscribe);
      };
    } catch (error) {
      console.error("Error setting up invitations listener:", error);
      // Only show critical connection errors
      if (error.code && error.code !== 'permission-denied' && showInvitationErrors) {
        setError("Failed to connect to the database. Please try again later.");
      }
    }
  }, [currentUser, activeSession, showInvitations, error, showInvitationErrors]);
  
  // Listen for active session updates
  useEffect(() => {
    if (!activeSession) {
      // Clear timer and markers when no active session
      setMarkers([]);
      setSelectedMarkers([]);
      setShowGlobalTimerNotification(null); // Clear notification
      
      // Make sure animation frames are properly canceled
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      setTimerRunning(false);
      return;
    }
    
    // Add debug logging for count-in tracking
    if (elapsedTime < 0 && timerRunning) {
      console.log(`Count-in in progress: ${formatTime(elapsedTime)}`);
    }
    
    try {
      // Set up the document listener
      const sessionUnsubscribe = onSnapshot(
        doc(db, "freeflows", activeSession.id), 
        (snapshot) => {
          if (snapshot.exists()) {
            const sessionData = { id: snapshot.id, ...snapshot.data() };
            
            // IMPORTANT: Save the previous session data BEFORE updating
            const previousSessionStatus = activeSession.status;
            const previousResetTime = activeSession.resetTime?.toMillis?.() || 0;
            const currentResetTime = sessionData.resetTime?.toMillis?.() || 0;
            const previousStopTime = activeSession.stopTime?.toMillis?.() || 0;
            const currentStopTime = sessionData.stopTime?.toMillis?.() || 0;
            const wasCountingIn = activeSession.isCountingIn;
            const isCountingIn = sessionData.isCountingIn;
            
            // Check if this user is a participant (not the creator)
            const isParticipant = activeSession.creatorId !== currentUser.uid;
            
            // Debug log for session status changes
            if (previousSessionStatus !== sessionData.status) {
              console.log(`Session status changed: ${previousSessionStatus} -> ${sessionData.status}`);
            }
            
            // Check if count-in state changed
            if (wasCountingIn !== isCountingIn) {
              console.log(`Count-in state changed: ${wasCountingIn} -> ${isCountingIn}`);
            }
            
            // Check if this is a reset action - IMPROVED LOGIC
            // Only consider it a reset if we're going from running to paused with time 0
            // AND it's not just a timer start (which would be paused to running)
            const isResetAction = 
              (currentResetTime > previousResetTime) || 
              (previousSessionStatus === "running" && 
               sessionData.status === "paused" && 
               sessionData.elapsedTimeBeforePause === 0);
            
            // Check if this is a stop action  
            // Only if going from running to paused with time > 0
            const isStopAction = 
              (currentStopTime > previousStopTime) || 
              (previousSessionStatus === "running" && 
               sessionData.status === "paused" && 
               sessionData.elapsedTimeBeforePause > 0);
            
            // Check if timer is starting (paused to running)
            const isTimerStarting = 
              previousSessionStatus === "paused" && sessionData.status === "running";
            
            // Show notifications to participants when global actions occur
            if (isParticipant) {
              if (isTimerStarting) {
                // Clear any notifications when timer starts
                console.log("Global timer started - clearing notifications for participant");
                setShowGlobalTimerNotification(null);
              } else if (isResetAction) {
                console.log("Global reset detected for participant - showing notification");
                setShowGlobalTimerNotification({
                  type: 'reset',
                  message: 'The session creator has reset the timer for all participants',
                  timestamp: Date.now()
                });
              } else if (isStopAction) {
                console.log("Global stop detected for participant - showing notification");
                setShowGlobalTimerNotification({
                  type: 'stop',
                  message: 'The session creator has stopped the timer for all participants',
                  timestamp: Date.now()
                });
              }
            }
            
            // NOW update the session data (critical for timer sync)
            setActiveSession(sessionData);
            
            if (isResetAction && timerRunning) {
              console.log("Reset detected, stopping timer");
              // Force stop timer and reset immediately
              if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
              }
              setTimerRunning(false);
              setElapsedTime(0);
              return; // Skip other timer logic since we've handled the reset
            }
            
            if (isStopAction && timerRunning) {
              console.log("Stop detected, stopping timer");
              // Force stop timer
              if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
              }
              setTimerRunning(false);
              if (sessionData.elapsedTimeBeforePause !== undefined) {
                setElapsedTime(sessionData.elapsedTimeBeforePause);
              }
              return; // Skip other timer logic since we've handled the stop
            }
            
            // Always sync the elapsed time from Firestore when it's defined
            if (sessionData.elapsedTimeBeforePause !== undefined && !timerRunning) {
              setElapsedTime(sessionData.elapsedTimeBeforePause);
            }
            
            // Handle timer state changes - THIS IS CRITICAL FOR PARTICIPANT SYNC
            if (sessionData.status === "running") {
              // If this client is starting a timer that wasn't running before,
              // or if count-in state changed, restart the timer
              if (previousSessionStatus !== "running" || wasCountingIn !== isCountingIn) {
                console.log("Session running state changed, restarting timer");
                if (timerRef.current) {
                  clearInterval(timerRef.current);
                  timerRef.current = null;
                }
                
                // Check if this user is a participant (use sessionData, not activeSession)
                const isParticipant = sessionData.creatorId !== currentUser.uid;
                
                console.log(`Timer starting - isParticipant: ${isParticipant}, startedAt: ${sessionData.startedAt}, status: ${sessionData.status}`);
                
                if (sessionData.startedAt) {
                  const now = Date.now();
                  const creatorStartTime = sessionData.startedAt;
                  const countInMs = (sessionData.countInDuration || 0) * 1000;
                  const baseElapsedTime = sessionData.elapsedTimeBeforePause || 0;
                  
                  // For participants, start fresh when status changes - ignore network delays
                  console.log("Participant starting fresh - using local start time");
                  
                  // Use our own start time reference point
                  const participantStartTime = now;
                  
                  let correctElapsedTime;
                  const shouldCountIn = sessionData.isCountingIn;
                  
                  if (shouldCountIn) {
                    correctElapsedTime = -countInMs;  // Start count-in from full duration
                  } else {
                    correctElapsedTime = baseElapsedTime;  // Start from the base elapsed time
                  }
                  
                  console.log(`Participant fresh start: ${correctElapsedTime}ms (ignoring creator's ${now - creatorStartTime}ms delay)`);
                  
                  // Set the calculated time immediately
                  setElapsedTime(correctElapsedTime);
                  setTimerRunning(true);
                  
                  // Start the timer interval using our own start time
                  timerRef.current = setInterval(() => {
                    const now = Date.now();
                    const participantTimeSinceStart = now - participantStartTime; // Use our start time, not creator's
                    
                    if (shouldCountIn) {
                      if (participantTimeSinceStart < countInMs) {
                        const countInRemaining = -countInMs + participantTimeSinceStart;
                        setElapsedTime(countInRemaining);
                      } else {
                        const timeAfterCountIn = participantTimeSinceStart - countInMs;
                        setElapsedTime(baseElapsedTime + timeAfterCountIn);
                      }
                    } else {
                      setElapsedTime(baseElapsedTime + participantTimeSinceStart);
                    }
                  }, 30);
                }
              } else {
                // For creators, use the original logic
                console.log("Creator detected - using normal start logic");
                startTimer(sessionData);
              }
            } else {
              // Stop the timer without updating Firestore to prevent loops
              if (timerRunning) {
                console.log("Session not running, stopping timer");
                stopTimer(false);
              }
            }
            
            setError(null);
          }
        },
        (error) => {
          console.error("Error listening to active session:", error);
          setError("Failed to sync with the current session. Please try again later.");
        }
      );
      
      // Set up listener for markers subcollection
      const markersQuery = query(
        collection(db, "freeflows", activeSession.id, "markers")
      );
      
      const markersUnsubscribe = onSnapshot(
        markersQuery,
        (snapshot) => {
          const markersData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setMarkers(markersData);
        },
        (error) => {
          console.error("Error listening to markers:", error);
          setError("Failed to sync markers. Please try again later.");
        }
      );
      
      unsubscribes.current.push(sessionUnsubscribe, markersUnsubscribe);
      
      return () => {
        sessionUnsubscribe();
        markersUnsubscribe();
        unsubscribes.current = unsubscribes.current.filter(fn => fn !== sessionUnsubscribe && fn !== markersUnsubscribe);
        
        // Ensure animation frames are canceled when component unmounts or active session changes
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        
        setTimerRunning(false);
      };
    } catch (error) {
      console.error("Error setting up active session listener:", error);
      setError("Failed to connect to the session. Please try again later.");
    }
  }, [activeSession?.id]); // Keep dependencies minimal

  // Timer functions
  const startTimer = (session) => {
    if (!session || !session.id) return;
    
    try {
      console.log("Starting timer with session:", {
        id: session.id,
        status: session.status,
        elapsedTimeBeforePause: session.elapsedTimeBeforePause,
        countInDuration: session.countInDuration
      });
      
      // Set local timer state first
      setTimerRunning(true);
      lastUpdateTimeRef.current = Date.now();
      
      const currentElapsedTime = session.elapsedTimeBeforePause || 0;
      const countInSeconds = session.countInDuration || 0;
      
      // Determine if we need to do a count-in based on current elapsed time
      // Only do count-in if we're starting from zero
      const shouldCountIn = countInSeconds > 0 && currentElapsedTime === 0;
      
      // Calculate our local offset for better synchronization
      const startTimeMs = Date.now();
      
      // Start sync with Firestore (if not already running)
      if (session.status !== "running") {
        console.log(`Starting timer with elapsed time: ${currentElapsedTime}ms${shouldCountIn ? `, count-in: ${countInSeconds}s` : ''}`);
        updateDoc(doc(db, "freeflows", session.id), {
          status: "running",
          startTime: serverTimestamp(),
          lastUpdateTime: serverTimestamp(),
          elapsedTimeBeforePause: currentElapsedTime,
          startedAt: startTimeMs, // Store client-side timestamp for more reliable calculations
          isCountingIn: shouldCountIn,
          countInStartedAt: shouldCountIn ? startTimeMs : null
        }).catch(error => {
          console.error("Error updating session status:", error);
          setError("Failed to start the timer. Please try again.");
          setTimerRunning(false);
        });
      }
      
      // Clear any existing timer
      if (timerRef.current) {
        console.log("Clearing existing timer interval");
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      // Set the initial elapsed time immediately
      if (shouldCountIn) {
        // Start at negative time for count-in
        setElapsedTime(-countInSeconds * 1000);
      } else {
        setElapsedTime(currentElapsedTime);
      }
      
      // Use setInterval for continuous, reliable updates
      timerRef.current = setInterval(() => {
        const now = Date.now();
        
        if (shouldCountIn) {
          // During count-in, we start from negative time and count up to zero
          const timeElapsedSinceStart = now - startTimeMs;
          const countInTimeMs = countInSeconds * 1000;
          
          // If we're still in count-in phase
          if (timeElapsedSinceStart < countInTimeMs) {
            const countInRemaining = -countInTimeMs + timeElapsedSinceStart;
            setElapsedTime(countInRemaining);
          } else {
            // Count-in finished, now start the actual timer
            const timeAfterCountIn = timeElapsedSinceStart - countInTimeMs;
            setElapsedTime(currentElapsedTime + timeAfterCountIn);
          }
        } else {
          // Normal timing without count-in
          const timeSinceStart = now - startTimeMs;
          const elapsed = currentElapsedTime + timeSinceStart;
          setElapsedTime(elapsed);
        }
      }, 30); // Update about 33 times per second for smooth animation
    } catch (error) {
      console.error("Error starting timer:", error);
      setError("Failed to start the timer. Please try again.");
      setTimerRunning(false);
    }
  };
  
  const stopTimer = (syncToFirestore = true) => {
    if (!activeSession) return;
    
    try {
      // Clear the interval
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      setTimerRunning(false);
      
      // Update Firestore only if we need to sync (i.e., initiated by the creator)
      if (syncToFirestore) {
        console.log("Stopping timer for all participants");
        updateDoc(doc(db, "freeflows", activeSession.id), {
          status: "paused",
          pauseTime: serverTimestamp(),
          stopTime: serverTimestamp(), // Add a timestamp to ensure the update is detected
          elapsedTimeBeforePause: elapsedTime
        }).catch(error => {
          console.error("Error updating session status:", error);
          setError("Failed to stop the timer. Please try again.");
        });
      }
    } catch (error) {
      console.error("Error stopping timer:", error);
      setError("Failed to stop the timer. Please try again.");
      // Ensure local state is consistent
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setTimerRunning(false);
    }
  };
  
  const resetTimer = (syncToFirestore = true) => {
    if (!activeSession) return;
    
    try {
      // Always stop the timer first
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      setTimerRunning(false);
      
      // Immediately reset elapsed time to 0
      setElapsedTime(0);
      
      // Clear global timer notification for participants when they reset locally
      if (!syncToFirestore && showGlobalTimerNotification) {
        setShowGlobalTimerNotification(null);
        console.log("Participant cleared global timer notification by resetting locally");
      }
      
      // Update Firestore only if we need to sync (i.e., initiated by the creator)
      if (syncToFirestore) {
        console.log("Resetting timer for all participants");
        updateDoc(doc(db, "freeflows", activeSession.id), {
          status: "paused",
          elapsedTimeBeforePause: 0,
          resetTime: serverTimestamp() // Add a timestamp to ensure the update is detected
        }).catch(error => {
          console.error("Error resetting timer:", error);
          setError("Failed to reset the timer. Please try again.");
        });
      }
    } catch (error) {
      console.error("Error resetting timer:", error);
      setError("Failed to reset the timer. Please try again.");
      // Ensure UI is consistent
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setTimerRunning(false);
      setElapsedTime(0);
    }
  };
  
  const setCustomTime = () => {
    if (!activeSession || !customStartTime) return;
    
    const timeMs = parseTimeToMs(customStartTime);
    if (timeMs === null) {
      setError("Please enter a valid time (e.g. 1:30 or 90)");
      return;
    }
    
    try {
      // Stop timer if running
      if (timerRunning) {
        clearInterval(timerRef.current);
        setTimerRunning(false);
      }
      
      // Set elapsed time
      setElapsedTime(timeMs);
      
      // Update Firestore
      updateDoc(doc(db, "freeflows", activeSession.id), {
        status: "paused",
        elapsedTimeBeforePause: timeMs
      }).catch(error => {
        console.error("Error setting custom time:", error);
        setError("Failed to set custom time. Please try again.");
      });
      
      // Clear input
      setCustomStartTime("");
    } catch (error) {
      console.error("Error setting custom time:", error);
      setError("Failed to set custom time. Please try again.");
      // Ensure UI is consistent
      if (timerRunning) {
        clearInterval(timerRef.current);
        setTimerRunning(false);
      }
      setElapsedTime(timeMs);
    }
  };
  
  // Session functions
  const createNewSession = async () => {
    if (!currentUser || !newSessionName.trim()) return;
    
    try {
      const newSession = {
        name: newSessionName.trim(),
        creatorId: currentUser.uid,
        participants: [currentUser.uid],
        createdAt: serverTimestamp(),
        countInDuration: countInDuration,
        status: "paused",
        elapsedTimeBeforePause: 0
      };
      
      const docRef = await addDoc(collection(db, "freeflows"), newSession);
      
      // Set the active session and close modal
      setActiveSession({ id: docRef.id, ...newSession });
      setShowCreateSession(false);
      setNewSessionName("");
      setError(null);
    } catch (error) {
      console.error("Error creating session:", error);
      setError("Failed to create session. Please try again.");
    }
  };
  
  // Marker functions
  const addMarker = async () => {
    if (!activeSession || !newMarkerName.trim() || !newMarkerTime) return;
    
    const timeMs = parseTimeToMs(newMarkerTime);
    if (timeMs === null) {
      setError("Please enter a valid time (e.g. 1:30 or 90)");
      return;
    }
    
    try {
      const countdownDuration = newMarkerCountdown.trim() ? 
        parseInt(newMarkerCountdown, 10) : null;
      
      const newMarker = {
        name: newMarkerName.trim(),
        timeMs,
        countdownDuration,
        createdAt: serverTimestamp()
      };
      
      // Add marker to subcollection
      const docRef = await addDoc(
        collection(db, "freeflows", activeSession.id, "markers"), 
        newMarker
      );
      
      // Save vibration preference if enabled
      if (newMarkerVibration) {
        setMarkerVibrations(prev => ({
          ...prev,
          [docRef.id]: true
        }));
        
        // Also save to localStorage
        try {
          const storageKey = `flowframe_vibrations_${currentUser?.uid}_${activeSession.id}`;
          const currentPrefs = JSON.parse(localStorage.getItem(storageKey) || "{}");
          currentPrefs[docRef.id] = true;
          localStorage.setItem(storageKey, JSON.stringify(currentPrefs));
        } catch (error) {
          console.warn("Failed to save new marker vibration setting:", error);
        }
      }
      
      // Reset form
      setNewMarkerName("");
      setNewMarkerTime("");
      setNewMarkerCountdown("");
      setNewMarkerVibration(false);
      setShowAddMarker(false);
      setError(null);
    } catch (error) {
      console.error("Error adding marker:", error);
      setError("Failed to add marker. Please try again.");
    }
  };
  
  const deleteMarker = async (markerId) => {
    if (!activeSession) return;
    
    try {
      // Remove from selected markers if needed
      setSelectedMarkers(prev => prev.filter(id => id !== markerId));
      
      // Delete marker from subcollection
      await deleteDoc(doc(db, "freeflows", activeSession.id, "markers", markerId));
      
      setError(null);
    } catch (error) {
      console.error("Error deleting marker:", error);
      setError("Failed to delete marker. Please try again.");
    }
  };
  
  // Modify the toggleMarkerSelection function to use localStorage instead of Firestore
  const toggleMarkerSelection = (markerId) => {
    setSelectedMarkers(prev => {
      const newSelections = prev.includes(markerId) 
        ? prev.filter(id => id !== markerId)
        : [...prev, markerId];
      
      // Save to localStorage if session exists
      if (activeSession?.id) {
        try {
          // Create a localStorage key specific to this user and session
          const storageKey = `flowframe_selections_${currentUser?.uid}_${activeSession.id}`;
          localStorage.setItem(storageKey, JSON.stringify(newSelections));
        } catch (error) {
          console.warn("Failed to save marker selections to localStorage:", error);
          // Don't show error to user as this is a non-critical operation
        }
      }
      
      return newSelections;
    });
  };
  
  // Load saved marker selections from localStorage
  useEffect(() => {
    if (activeSession?.id && currentUser?.uid && markers.length > 0) {
      try {
        // Get the localStorage key specific to this user and session
        const storageKey = `flowframe_selections_${currentUser.uid}_${activeSession.id}`;
        const savedSelectionsStr = localStorage.getItem(storageKey);
        
        if (savedSelectionsStr) {
          const savedSelections = JSON.parse(savedSelectionsStr);
          
          // Only use valid marker IDs that still exist in the session
          const validSelections = Array.isArray(savedSelections) 
            ? savedSelections.filter(id => markers.some(marker => marker.id === id))
            : [];
            
          if (validSelections.length > 0) {
            setSelectedMarkers(validSelections);
            console.log("Loaded saved marker selections from localStorage");
          }
        }
      } catch (error) {
        console.warn("Failed to load marker selections from localStorage:", error);
        // Non-critical error, don't show to user
      }
    }
  }, [activeSession?.id, currentUser?.uid, markers]);
  
  const openEditMarker = (marker, e) => {
    e.stopPropagation();
    setEditingMarker(marker);
    setEditMarkerName(marker.name);
    setEditMarkerTime(formatTime(marker.timeMs));
    setEditMarkerCountdown(marker.countdownDuration?.toString() || "");
    setEditMarkerVibration(!!markerVibrations[marker.id]);
    setShowEditMarker(true);
  };
  
  const saveEditedMarker = async () => {
    if (!activeSession || !editingMarker || !editMarkerName.trim() || !editMarkerTime) return;
    
    const timeMs = parseTimeToMs(editMarkerTime);
    if (timeMs === null) {
      setError("Please enter a valid time (e.g. 1:30 or 90)");
      return;
    }
    
    try {
      const countdownDuration = editMarkerCountdown.trim() ? 
        parseInt(editMarkerCountdown, 10) : null;
      
      // Update marker directly in subcollection
      await updateDoc(
        doc(db, "freeflows", activeSession.id, "markers", editingMarker.id), 
        {
          name: editMarkerName.trim(),
          timeMs,
          countdownDuration,
          updatedAt: serverTimestamp()
        }
      );
      
      // Update vibration setting
      setMarkerVibrations(prev => ({
        ...prev,
        [editingMarker.id]: editMarkerVibration
      }));
      
      // Also save to localStorage
      try {
        const storageKey = `flowframe_vibrations_${currentUser?.uid}_${activeSession.id}`;
        const currentPrefs = JSON.parse(localStorage.getItem(storageKey) || "{}");
        currentPrefs[editingMarker.id] = editMarkerVibration;
        localStorage.setItem(storageKey, JSON.stringify(currentPrefs));
      } catch (error) {
        console.warn("Failed to save edited marker vibration setting:", error);
      }
      
      // Reset form and close modal
      setShowEditMarker(false);
      setEditingMarker(null);
      setEditMarkerName("");
      setEditMarkerTime("");
      setEditMarkerCountdown("");
      setEditMarkerVibration(false);
      setError(null);
    } catch (error) {
      console.error("Error updating marker:", error);
      setError("Failed to update marker. Please try again.");
    }
  };
  
  // Invitation functions
  const inviteUser = async () => {
    if (!activeSession || !inviteEmail.trim()) return;
    
    try {
      // Start showing invitation errors after user tries to interact with invitations
      setShowInvitationErrors(true);
      
      console.log(`Attempting to invite ${inviteEmail.trim()} to session ${activeSession.id}`);
      
      // Check if there's already a pending invitation
      try {
        const q = query(
          collection(db, "invitations"),
          where("sessionId", "==", activeSession.id),
          where("recipientEmail", "==", inviteEmail.trim()),
          where("status", "==", "pending")
        );
        
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          setError("This user already has a pending invitation");
          return;
        }
        
        console.log("No existing invitation found, creating new invitation");
      } catch (queryError) {
        console.error("Error checking existing invitations:", queryError);
        // Continue with creation even if query fails - worst case is duplicate invitation
      }
      
      // Create the invitation
      const invitation = {
        sessionId: activeSession.id,
        sessionName: activeSession.name,
        creatorId: currentUser.uid,
        creatorEmail: currentUser.email,
        creatorName: currentUser.displayName || currentUser.email.split('@')[0],
        recipientEmail: inviteEmail.trim(),
        status: "pending",
        createdAt: serverTimestamp()
      };
      
      console.log("Creating invitation with data:", { ...invitation, createdAt: "serverTimestamp()" });
      
      await addDoc(collection(db, "invitations"), invitation);
      console.log("Invitation created successfully");
      
      // Clear form and error
      setInviteEmail("");
      setError(null);
    } catch (error) {
      console.error("Error inviting user:", error);
      
      // Provide more specific error messages
      if (error.code === "permission-denied") {
        setError("Permission denied. Please try refreshing the page and trying again.");
      } else {
        setError(`Failed to invite user: ${error.message}`);
      }
    }
  };
  
  const handleInvitation = async (invitationId, accept) => {
    try {
      // Start showing invitation errors after user tries to interact with invitations
      setShowInvitationErrors(true);
      
      const invitation = pendingInvitations.find(inv => inv.id === invitationId);
      if (!invitation) return;
      
      const inviteRef = doc(db, "invitations", invitationId);
      
      if (accept) {
        // Add user to session participants
        const sessionRef = doc(db, "freeflows", invitation.sessionId);
        await updateDoc(sessionRef, {
          participants: arrayUnion(currentUser.uid)
        });
        
        // Update invitation status
        await updateDoc(inviteRef, {
          status: "accepted"
        });
        
        // Load the session
        const sessionDoc = await getDoc(sessionRef);
        if (sessionDoc.exists()) {
          setActiveSession({ id: sessionDoc.id, ...sessionDoc.data() });
          setShowInvitations(false);
        }
      } else {
        // Update invitation status
        await updateDoc(inviteRef, {
          status: "declined"
        });
      }
      
      setError(null);
    } catch (error) {
      console.error("Error handling invitation:", error);
      setError("Failed to process invitation. Please try again.");
    }
  };
  
  // Add function to get personalized marker data
  const getPersonalizedMarker = useCallback((marker) => {
    const personalizedData = personalizedMarkers[marker.id];
    if (!personalizedData) return marker;
    
    return {
      ...marker,
      name: personalizedData.name || marker.name,
      countdownDuration: personalizedData.countdownDuration !== undefined 
        ? personalizedData.countdownDuration 
        : marker.countdownDuration
    };
  }, [personalizedMarkers]);

  // Function to save personalized marker settings
  const savePersonalizedMarker = () => {
    if (!activeSession || !personalizingMarker || !personalizeMarkerName.trim()) return;
    
    const countdownDuration = personalizeMarkerCountdown.trim() ? 
      parseInt(personalizeMarkerCountdown, 10) : null;
    
    const newPersonalizedData = {
      name: personalizeMarkerName.trim(),
      countdownDuration
    };
    
    setPersonalizedMarkers(prev => {
      const updated = {
        ...prev,
        [personalizingMarker.id]: newPersonalizedData
      };
      
      // Save to localStorage
      if (activeSession?.id && currentUser?.uid) {
        try {
          const storageKey = `flowframe_personalized_markers_${currentUser.uid}_${activeSession.id}`;
          localStorage.setItem(storageKey, JSON.stringify(updated));
        } catch (error) {
          console.warn("Failed to save personalized marker settings:", error);
        }
      }
      
      return updated;
    });
    
    // Reset form and close modal
    setShowPersonalizeMarker(false);
    setPersonalizingMarker(null);
    setPersonalizeMarkerName("");
    setPersonalizeMarkerCountdown("");
    setError(null);
  };

  // Function to open personalize marker modal
  const openPersonalizeMarker = (marker, e) => {
    e.stopPropagation();
    const personalizedData = personalizedMarkers[marker.id];
    
    setPersonalizingMarker(marker);
    setPersonalizeMarkerName(personalizedData?.name || marker.name);
    setPersonalizeMarkerCountdown(
      personalizedData?.countdownDuration !== undefined 
        ? personalizedData.countdownDuration?.toString() || ""
        : marker.countdownDuration?.toString() || ""
    );
    setShowPersonalizeMarker(true);
  };

  // Function to reset personalized marker to original
  const resetPersonalizedMarker = (markerId) => {
    setPersonalizedMarkers(prev => {
      const updated = { ...prev };
      delete updated[markerId];
      
      // Save to localStorage
      if (activeSession?.id && currentUser?.uid) {
        try {
          const storageKey = `flowframe_personalized_markers_${currentUser.uid}_${activeSession.id}`;
          localStorage.setItem(storageKey, JSON.stringify(updated));
        } catch (error) {
          console.warn("Failed to save personalized marker settings:", error);
        }
      }
      
      return updated;
    });
  };

  // Load personalized marker settings from localStorage
  useEffect(() => {
    if (activeSession?.id && currentUser?.uid) {
      try {
        const storageKey = `flowframe_personalized_markers_${currentUser.uid}_${activeSession.id}`;
        const savedPersonalizations = localStorage.getItem(storageKey);
        
        if (savedPersonalizations) {
          setPersonalizedMarkers(JSON.parse(savedPersonalizations));
        } else {
          setPersonalizedMarkers({});
        }
      } catch (error) {
        console.warn("Failed to load personalized marker settings:", error);
        setPersonalizedMarkers({});
      }
    } else {
      setPersonalizedMarkers({});
    }
  }, [activeSession?.id, currentUser?.uid]);
  
  // Memoize the marker countdowns to ensure consistent updates
  const countdowns = useMemo(() => {
    if (!selectedMarkers.length || !timerRunning) return [];
    
    return markers
      .filter(marker => selectedMarkers.includes(marker.id))
      .map(marker => {
        const personalizedMarker = getPersonalizedMarker(marker);
        const timeToMarker = personalizedMarker.timeMs - elapsedTime;
        return {
          ...personalizedMarker,
          countdown: timeToMarker > 0 ? timeToMarker : timeToMarker >= -1000 ? timeToMarker : null
        };
      })
      .filter(marker => marker.countdown !== null)
      .sort((a, b) => a.countdown - b.countdown);
  }, [markers, selectedMarkers, elapsedTime, timerRunning, getPersonalizedMarker]);
  
  // Use a memoized value for visual countdowns to ensure timer and progress sync
  const activeVisualCountdowns = useMemo(() => {
    // Still show countdowns even during count-in phase
    if (!timerRunning) return [];
    
    // Create a normalized elapsed time - if we're in count-in mode (negative time),
    // treat it as 0 for countdown calculation purposes
    const normalizedElapsedTime = elapsedTime < 0 ? 0 : elapsedTime;
    
    return markers
      .filter(marker => {
        const personalizedMarker = getPersonalizedMarker(marker);
        return personalizedMarker.countdownDuration;
      })
      .map(marker => {
        const personalizedMarker = getPersonalizedMarker(marker);
        const timeToMarker = personalizedMarker.timeMs - normalizedElapsedTime;
        const countdownMs = personalizedMarker.countdownDuration * 1000;
        
        if (timeToMarker > 0 && timeToMarker <= countdownMs) {
          const secondsLeft = Math.floor(timeToMarker / 1000) + 1;
          const totalSeconds = personalizedMarker.countdownDuration;
          const secondsElapsed = totalSeconds - secondsLeft;
          const discreteProgress = Math.max(0, Math.min(1, secondsElapsed / totalSeconds));
          
          if (elapsedTime < 0) {
            console.log(`Count-in active, showing marker countdown: ${personalizedMarker.name}, seconds left: ${secondsLeft}`);
          }
          
          return {
            ...personalizedMarker,
            timeRemaining: timeToMarker,
            progress: discreteProgress,
            secondsLeft,
            totalSeconds,
            duringCountIn: elapsedTime < 0
          };
        }
        return null;
      })
      .filter(item => item !== null)
      .sort((a, b) => a.timeRemaining - b.timeRemaining);
  }, [markers, elapsedTime, timerRunning, getPersonalizedMarker]);
  
  // Migrate existing markers if needed
  useEffect(() => {
    const migrateMarkers = async () => {
      // Only proceed if we have an active session
      if (!activeSession || !activeSession.id) return;
      
      try {
        console.log("Checking for markers migration needed...");
        
        // First check if this is a legacy session with markers array
        const sessionRef = doc(db, "freeflows", activeSession.id);
        const sessionDoc = await getDoc(sessionRef);
        
        if (!sessionDoc.exists()) return;
        
        const sessionData = sessionDoc.data();
        const hasLegacyMarkers = sessionData.markers && Array.isArray(sessionData.markers) && sessionData.markers.length > 0;
        
        if (!hasLegacyMarkers) {
          console.log("No legacy markers found, skipping migration");
          return;
        }
        
        // Check if markers already exist in subcollection to avoid duplicates
        const markersQuery = query(collection(db, "freeflows", activeSession.id, "markers"));
        const existingMarkers = await getDocs(markersQuery);
        
        if (!existingMarkers.empty) {
          console.log("Markers subcollection already exists, skipping migration");
          return;
        }
        
        console.log("Starting migration of markers to subcollection");
        
        // Start migrating markers from array to subcollection
        const migrationPromises = [];
        
        for (const marker of sessionData.markers) {
          if (!marker || !marker.id) continue;
          
          const markerId = marker.id;
          const newMarker = {
            ...marker,
            createdAt: serverTimestamp()
          };
          
          // Remove id from the document data (it will be the document id)
          delete newMarker.id;
          
          migrationPromises.push(
            setDoc(doc(db, "freeflows", activeSession.id, "markers", markerId), newMarker)
          );
        }
        
        await Promise.all(migrationPromises);
        
        // Remove markers array from session document
        await updateDoc(sessionRef, {
          markers: deleteField()
        });
        
        console.log("Markers successfully migrated to subcollection");
      } catch (error) {
        console.error("Error migrating markers:", error);
      }
    };
    
    migrateMarkers().catch(error => {
      console.error("Migration failed:", error);
    });
  }, [activeSession?.id]);

  // Delete session state
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  
  // Delete session function
  const deleteSession = async () => {
    if (!sessionToDelete) return;
    
    try {
      const sessionId = sessionToDelete.id;
      console.log(`Starting deletion of session ${sessionId}`);
      
      // First check if user has permission to delete (must be creator)
      if (sessionToDelete.creatorId !== currentUser.uid) {
        console.error("Permission denied: User is not the creator of this session");
        setError("You don't have permission to delete this session");
        return;
      }
      
      console.log(`User ${currentUser.uid} is authorized to delete session ${sessionId}`);
      
      // Due to potential permission issues with the batch approach, let's try each step separately
      
      // 1. Delete all markers in the subcollection
      try {
        console.log(`Fetching markers for session ${sessionId}...`);
        const markersQuery = query(collection(db, "freeflows", sessionId, "markers"));
        const markersSnapshot = await getDocs(markersQuery);
        
        console.log(`Found ${markersSnapshot.size} markers to delete`);
        
        // Delete markers one by one
        for (const markerDoc of markersSnapshot.docs) {
          await deleteDoc(doc(db, "freeflows", sessionId, "markers", markerDoc.id));
        }
        
        console.log(`Deleted ${markersSnapshot.size} markers from session ${sessionId}`);
      } catch (markerError) {
        console.error("Error deleting markers:", markerError);
        if (markerError.code === "permission-denied") {
          setError("Permission denied for marker deletion. Please try refreshing the page.");
        } else {
          setError(`Failed to delete session markers: ${markerError.message}`);
        }
        return;
      }
      
      // 2. Delete the session document
      try {
        const sessionRef = doc(db, "freeflows", sessionId);
        await deleteDoc(sessionRef);
        console.log(`Successfully deleted session ${sessionId}`);
      } catch (sessionError) {
        console.error("Error deleting session document:", sessionError);
        if (sessionError.code === "permission-denied") {
          setError("Permission denied for session deletion. Please try refreshing the page.");
        } else {
          setError(`Failed to delete session document: ${sessionError.message}`);
        }
        return;
      }
      
      // 3. Delete invitations (best effort, don't block if this fails)
      try {
        console.log(`Attempting to delete invitations for session ${sessionId}...`);
        const invitationsQuery = query(
          collection(db, "invitations"), 
          where("sessionId", "==", sessionId),
          where("status", "==", "pending")
        );
        
        const invitationsSnapshot = await getDocs(invitationsQuery);
        console.log(`Found ${invitationsSnapshot.size} invitations to delete`);
        
        for (const inviteDoc of invitationsSnapshot.docs) {
          try {
            await deleteDoc(doc(db, "invitations", inviteDoc.id));
          } catch (individualInviteError) {
            console.warn(`Failed to delete invitation ${inviteDoc.id}:`, individualInviteError);
            // Continue with others even if one fails
          }
        }
      } catch (inviteError) {
        // Just log the error but don't prevent the deletion from being "successful"
        console.error("Error processing invitations:", inviteError);
      }
      
      // Reset state
      setSessionToDelete(null);
      setShowDeleteConfirmation(false);
      setError(null);
    } catch (error) {
      console.error("Error in delete session flow:", error);
      setError(`Failed to delete session: ${error.message}`);
    }
  };

  // Error display component
  const ErrorMessage = () => error ? (
    <div className="p-3 mb-4 bg-red-100 border border-red-400 text-red-700 rounded">
      <p>{error}</p>
    </div>
  ) : null;

  // Add a timer sync interval to keep all clients in sync over time
  useEffect(() => {
    if (!activeSession || !timerRunning || !activeSession.startedAt) return;
    
    // Create a timer that periodically checks and corrects for drift
    const syncIntervalId = setInterval(() => {
      try {
        const now = Date.now();
        const startTime = activeSession.startedAt;
        const countInMs = (activeSession.countInDuration || 0) * 1000;
        const baseElapsedTime = activeSession.elapsedTimeBeforePause || 0;
        
        // Calculate what the time should be based on the start timestamp
        const timeSinceStart = now - startTime;
        
        // If we're in count-in phase
        if (activeSession.isCountingIn && timeSinceStart < countInMs) {
          const countInRemaining = -countInMs + timeSinceStart;
          
          // If the current elapsed time is significantly different, correct it
          if (Math.abs(countInRemaining - elapsedTime) > 500) {
            console.log(`Syncing count-in time: ${elapsedTime}ms -> ${countInRemaining}ms`);
            setElapsedTime(countInRemaining);
          }
        } 
        // Regular timing phase
        else {
          // If count-in was active, account for it in the calculation
          const actualElapsedTime = activeSession.isCountingIn
            ? baseElapsedTime + Math.max(0, timeSinceStart - countInMs)
            : baseElapsedTime + timeSinceStart;
          
          // If the current elapsed time is significantly different, correct it
          if (Math.abs(actualElapsedTime - elapsedTime) > 500) {
            console.log(`Syncing timer: ${elapsedTime}ms -> ${actualElapsedTime}ms`);
            setElapsedTime(actualElapsedTime);
          }
        }
      } catch (error) {
        console.error("Error in timer sync interval:", error);
      }
    }, 5000); // Check every 5 seconds
    
    return () => clearInterval(syncIntervalId);
  }, [activeSession, timerRunning, elapsedTime]);

  // First, add a state to track vibration preferences
  const [markerVibrations, setMarkerVibrations] = useState({});
  
  // Near the toggle marker selection function, add a toggle vibration function
  const toggleMarkerVibration = (e, markerId) => {
    // Stop the event from triggering marker selection
    e.stopPropagation();
    
    setMarkerVibrations(prev => {
      const newVibrations = {
        ...prev,
        [markerId]: !prev[markerId]
      };
      
      // Save vibration preferences to localStorage
      if (activeSession?.id) {
        try {
          const storageKey = `flowframe_vibrations_${currentUser?.uid}_${activeSession.id}`;
          localStorage.setItem(storageKey, JSON.stringify(newVibrations));
        } catch (error) {
          console.warn("Failed to save vibration preferences to localStorage:", error);
        }
      }
      
      return newVibrations;
    });
  };
  
  // Add an effect to load saved vibration preferences
  useEffect(() => {
    if (activeSession?.id && currentUser?.uid) {
      try {
        // Load vibration preferences from localStorage
        const storageKey = `flowframe_vibrations_${currentUser?.uid}_${activeSession.id}`;
        const savedVibrations = localStorage.getItem(storageKey);
        
        if (savedVibrations) {
          setMarkerVibrations(JSON.parse(savedVibrations));
        }
      } catch (error) {
        console.warn("Failed to load vibration preferences from localStorage:", error);
      }
    }
  }, [activeSession?.id, currentUser?.uid]);
  
  // Add a vibration function that checks browser support
  const vibrateDevice = useCallback((pattern) => {
    // Check if vibration API is supported
    if ('vibrate' in navigator) {
      try {
        navigator.vibrate(pattern);
      } catch (error) {
        console.warn('Vibration failed:', error);
      }
    }
  }, []);
  
  // Add effect to check for markers that have just been reached and should vibrate
  useEffect(() => {
    if (!timerRunning) return;
    
    // Find any markers that were just reached (between 0 and -500ms)
    const justReachedMarkers = markers
      .filter(marker => {
        const timeToMarker = marker.timeMs - elapsedTime;
        return timeToMarker <= 0 && timeToMarker > -500 && markerVibrations[marker.id];
      });
    
    // Vibrate for each marker that was just reached and has vibration enabled
    if (justReachedMarkers.length > 0) {
      // Use a stronger vibration if multiple markers are reached simultaneously
      const vibrationStrength = Math.min(justReachedMarkers.length * 200, 1000);
      vibrateDevice([100, 50, vibrationStrength]);
    }
  }, [elapsedTime, markers, markerVibrations, timerRunning, vibrateDevice]);
  
  // Add state variables for new marker vibration in the component (near the other state variables)
  const [newMarkerVibration, setNewMarkerVibration] = useState(false);
  const [editMarkerVibration, setEditMarkerVibration] = useState(false);
  
  // Add state for wake lock
  const [wakeLock, setWakeLock] = useState(null);
  const [wakeLockSupported, setWakeLockSupported] = useState(false);
  const [wakeLockStatus, setWakeLockStatus] = useState("inactive");
  const wakeLockStatusTimerRef = useRef(null);
  
  // Update wake lock status with debounce to prevent flickering
  const updateWakeLockStatus = useCallback((newStatus) => {
    // Clear any pending status update
    if (wakeLockStatusTimerRef.current) {
      clearTimeout(wakeLockStatusTimerRef.current);
    }
    
    // Set the new status after a short delay to debounce rapid changes
    wakeLockStatusTimerRef.current = setTimeout(() => {
      setWakeLockStatus(newStatus);
      wakeLockStatusTimerRef.current = null;
    }, 300);
  }, []);
  
  // Check if Screen Wake Lock API is supported
  useEffect(() => {
    if ('wakeLock' in navigator) {
      setWakeLockSupported(true);
    } else {
      console.log("Screen Wake Lock API not supported on this browser");
      setWakeLockSupported(false);
    }
  }, []);
  
  // Function to request wake lock
  const requestWakeLock = useCallback(async () => {
    if (!wakeLockSupported) return;
    
    try {
      // Update status to attempting
      updateWakeLockStatus("attempting");
      
      // Request a screen wake lock
      const lock = await navigator.wakeLock.request('screen');
      setWakeLock(lock);
      updateWakeLockStatus("active");
      console.log("Wake Lock acquired");
      
      // Listen for wake lock release
      lock.addEventListener('release', () => {
        console.log("Wake Lock released");
        setWakeLock(null);
        updateWakeLockStatus("inactive");
      });
    } catch (err) {
      console.error(`Failed to acquire Wake Lock: ${err.message}`);
      updateWakeLockStatus("error");
      setTimeout(() => updateWakeLockStatus("inactive"), 3000);
    }
  }, [wakeLockSupported, updateWakeLockStatus]);
  
  // Function to release wake lock
  const releaseWakeLock = useCallback(() => {
    if (wakeLock) {
      wakeLock.release()
        .then(() => {
          console.log("Wake Lock released manually");
          setWakeLock(null);
          updateWakeLockStatus("inactive");
        })
        .catch((err) => {
          console.error(`Failed to release Wake Lock: ${err.message}`);
          updateWakeLockStatus("error");
          setTimeout(() => updateWakeLockStatus("inactive"), 3000);
        });
    }
  }, [wakeLock, updateWakeLockStatus]);
  
  // Clean up wake lock status timer on unmount
  useEffect(() => {
    return () => {
      if (wakeLockStatusTimerRef.current) {
        clearTimeout(wakeLockStatusTimerRef.current);
      }
    };
  }, []);
  
  // Request wake lock when timer starts, release when stops
  useEffect(() => {
    if (timerRunning && activeSession) {
      requestWakeLock();
    } else if (wakeLock) {
      releaseWakeLock();
    }
    
    // Re-acquire wake lock when the page becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && timerRunning) {
        requestWakeLock();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLock) releaseWakeLock();
    };
  }, [timerRunning, activeSession, wakeLock, requestWakeLock, releaseWakeLock]);
  
  // Function to toggle controls lock
  const toggleControlsLock = () => {
    setIsControlsLocked(prev => {
      const newLockState = !prev;
      
      // Save to localStorage
      if (currentUser?.uid) {
        try {
          const storageKey = `flowframe_controls_locked_${currentUser.uid}`;
          localStorage.setItem(storageKey, JSON.stringify(newLockState));
        } catch (error) {
          console.warn("Failed to save controls lock state:", error);
        }
      }
      
      return newLockState;
    });
  };

  // Load controls lock state from localStorage
  useEffect(() => {
    if (currentUser?.uid) {
      try {
        const storageKey = `flowframe_controls_locked_${currentUser.uid}`;
        const savedLockState = localStorage.getItem(storageKey);
        
        if (savedLockState !== null) {
          setIsControlsLocked(JSON.parse(savedLockState));
        }
      } catch (error) {
        console.warn("Failed to load controls lock state:", error);
      }
    }
  }, [currentUser?.uid]);
  
  // Screen lock functions
  const toggleScreenLock = () => {
    setIsScreenLocked(prev => {
      const newLockState = !prev;
      
      // Save to localStorage
      if (currentUser?.uid) {
        try {
          const storageKey = `flowframe_screen_locked_${currentUser.uid}`;
          localStorage.setItem(storageKey, JSON.stringify(newLockState));
        } catch (error) {
          console.warn("Failed to save screen lock state:", error);
        }
      }
      
      return newLockState;
    });
  };

  const unlockScreen = () => {
    setIsScreenLocked(false);
    
    // Save to localStorage
    if (currentUser?.uid) {
      try {
        const storageKey = `flowframe_screen_locked_${currentUser.uid}`;
        localStorage.setItem(storageKey, JSON.stringify(false));
      } catch (error) {
        console.warn("Failed to save screen lock state:", error);
      }
    }
  };

  // Handle double tap for mobile unlock
  const handleScreenTap = (e) => {
    if (!isScreenLocked) return;
    
    const currentTime = Date.now();
    const timeSinceLastTap = currentTime - lastTapTime;
    
    // Clear any existing timeout
    if (doubleTapTimeoutRef.current) {
      clearTimeout(doubleTapTimeoutRef.current);
      doubleTapTimeoutRef.current = null;
    }
    
    // If this is within double-tap time window (500ms)
    if (timeSinceLastTap < 500 && timeSinceLastTap > 0) {
      // Double tap detected
      unlockScreen();
            } else {
      // First tap or too long since last tap
      setLastTapTime(currentTime);
      
      // Set timeout to reset the tap timing
      doubleTapTimeoutRef.current = setTimeout(() => {
        setLastTapTime(0);
        doubleTapTimeoutRef.current = null;
      }, 500);
    }
  };

  // Keyboard event handler for Ctrl-L
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Check for Ctrl-L (Windows/Linux) or Cmd-L (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'l') {
        e.preventDefault();
        if (isScreenLocked) {
          unlockScreen();
      } else {
          toggleScreenLock();
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isScreenLocked]);

  // Load screen lock state from localStorage
  useEffect(() => {
    if (currentUser?.uid) {
      try {
        const storageKey = `flowframe_screen_locked_${currentUser.uid}`;
        const savedLockState = localStorage.getItem(storageKey);
        
        if (savedLockState !== null) {
          setIsScreenLocked(JSON.parse(savedLockState));
        }
    } catch (error) {
        console.warn("Failed to load screen lock state:", error);
      }
    }
  }, [currentUser?.uid]);

  // Cleanup double tap timeout on unmount
  useEffect(() => {
    return () => {
      if (doubleTapTimeoutRef.current) {
        clearTimeout(doubleTapTimeoutRef.current);
      }
    };
  }, []);
  
  // Replace the existing resyncWithCreator function (around line 1692) with this improved version:
  const resyncWithCreator = async () => {
    if (!activeSession) return;
    
    try {
      // Stop any existing timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setTimerRunning(false);
      
      // Fetch the latest session state to ensure we have the most current data
      const sessionDoc = await getDoc(doc(db, "freeflows", activeSession.id));
      if (!sessionDoc.exists()) {
        setError("Session not found");
        return;
      }
      
      const latestSessionData = { id: sessionDoc.id, ...sessionDoc.data() };
      console.log("Resyncing with latest session data:", latestSessionData);
      
      // Update our local session state
      setActiveSession(latestSessionData);
      
      if (latestSessionData.status === "running" && latestSessionData.startedAt) {
        // Calculate what the elapsed time should be right now
        const now = Date.now();
        const startTime = latestSessionData.startedAt;
        const countInMs = (latestSessionData.countInDuration || 0) * 1000;
        const baseElapsedTime = latestSessionData.elapsedTimeBeforePause || 0;
        const timeSinceStart = now - startTime;
        
        let correctElapsedTime;
        
        // *** FIXED LOGIC: Check if this is a fresh start ***
        const isFreshStart = baseElapsedTime === 0;
        
        if (isFreshStart) {
          // For fresh starts, ignore network delay and sync to the same starting point
          if (latestSessionData.isCountingIn) {
            // Calculate where we should be in the count-in based on time elapsed
            if (timeSinceStart < countInMs) {
              correctElapsedTime = -countInMs + timeSinceStart;
            } else {
              correctElapsedTime = timeSinceStart - countInMs;
            }
          } else {
            correctElapsedTime = timeSinceStart;  // Use actual time elapsed for non-count-in
          }
        } else {
          // For resumes, use the existing logic
          if (latestSessionData.isCountingIn && timeSinceStart < countInMs) {
            correctElapsedTime = -countInMs + timeSinceStart;
          } else {
            correctElapsedTime = latestSessionData.isCountingIn
              ? baseElapsedTime + Math.max(0, timeSinceStart - countInMs)
              : baseElapsedTime + timeSinceStart;
          }
        }
        
        console.log(`Resyncing timer to: ${correctElapsedTime}ms (${isFreshStart ? 'fresh start' : 'resume'})`);
        
        // Set the correct elapsed time
        setElapsedTime(correctElapsedTime);
        
        // Create a modified session data with the correct elapsed time for startTimer
        const modifiedSessionData = {
          ...latestSessionData,
          elapsedTimeBeforePause: correctElapsedTime,
          // Force startTimer to use our calculated time, not the old database value
          status: "running" // Ensure it's marked as running
        };
        
        // Start the timer with the corrected data
        setTimerRunning(true);
        
        // Use the same timer logic as startTimer but with our corrected time
        const currentElapsedTime = correctElapsedTime;
        const countInSeconds = latestSessionData.countInDuration || 0;
        const shouldCountIn = latestSessionData.isCountingIn && timeSinceStart < countInMs;
        
        // Clear any existing timer (redundant but safe)
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        
        // Start the interval timer
        timerRef.current = setInterval(() => {
          const now = Date.now();
          const timeSinceStart = now - startTime;
          
          if (shouldCountIn) {
            // During count-in, we start from negative time and count up to zero
            const countInTimeMs = countInSeconds * 1000;
            
            // If we're still in count-in phase
            if (timeSinceStart < countInTimeMs) {
              const countInRemaining = -countInTimeMs + timeSinceStart;
              setElapsedTime(countInRemaining);
            } else {
              // Count-in finished, now start the actual timer
              const timeAfterCountIn = timeSinceStart - countInTimeMs;
              setElapsedTime(baseElapsedTime + timeAfterCountIn);
            }
          } else {
            // Normal timing without count-in
            const elapsed = baseElapsedTime + timeSinceStart;
            setElapsedTime(elapsed);
          }
        }, 30);
        
      } else {
        // Session is paused, just set the elapsed time
        const pausedElapsedTime = latestSessionData.elapsedTimeBeforePause || 0;
        console.log(`Resyncing paused timer to: ${pausedElapsedTime}ms`);
        setElapsedTime(pausedElapsedTime);
      }
      
      // Clear any global notifications since we're now in sync
      setShowGlobalTimerNotification(null);
      
      setError(null);
    } catch (error) {
      console.error("Error resyncing with creator:", error);
      setError("Failed to resync with creator's timer. Please try again.");
    }
  };
  
  // Add this new function near the other timer functions (around line 620):
  const startLocalTimer = (session) => {
    if (!session || !session.id) return;
    
    try {
      console.log("Starting LOCAL timer (participant only) with session:", {
        id: session.id,
        elapsedTimeBeforePause: session.elapsedTimeBeforePause,
        countInDuration: session.countInDuration
      });
      
      // Set local timer state first
      setTimerRunning(true);
      lastUpdateTimeRef.current = Date.now();
      
      const currentElapsedTime = session.elapsedTimeBeforePause || 0;
      const countInSeconds = session.countInDuration || 0;
      
      // Determine if we need to do a count-in based on current elapsed time
      // Only do count-in if we're starting from zero
      const shouldCountIn = countInSeconds > 0 && currentElapsedTime === 0;
      
      // Calculate our local offset for better synchronization
      const startTimeMs = Date.now();
      
      // DO NOT update Firestore - this is local only
      
      // Clear any existing timer
      if (timerRef.current) {
        console.log("Clearing existing timer interval");
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      // Set the initial elapsed time immediately
      if (shouldCountIn) {
        // Start at negative time for count-in
        setElapsedTime(-countInSeconds * 1000);
      } else {
        setElapsedTime(currentElapsedTime);
      }
      
      // Use setInterval for continuous, reliable updates
      timerRef.current = setInterval(() => {
        const now = Date.now();
        
        if (shouldCountIn) {
          // During count-in, we start from negative time and count up to zero
          const timeElapsedSinceStart = now - startTimeMs;
          const countInTimeMs = countInSeconds * 1000;
          
          // If we're still in count-in phase
          if (timeElapsedSinceStart < countInTimeMs) {
            const countInRemaining = -countInTimeMs + timeElapsedSinceStart;
            setElapsedTime(countInRemaining);
          } else {
            // Count-in finished, now start the actual timer
            const timeAfterCountIn = timeElapsedSinceStart - countInTimeMs;
            setElapsedTime(currentElapsedTime + timeAfterCountIn);
          }
        } else {
          // Normal timing without count-in
          const timeSinceStart = now - startTimeMs;
          const elapsed = currentElapsedTime + timeSinceStart;
          setElapsedTime(elapsed);
        }
      }, 30); // Update about 33 times per second for smooth animation
    } catch (error) {
      console.error("Error starting local timer:", error);
      setError("Failed to start the local timer. Please try again.");
      setTimerRunning(false);
    }
  };
  
  // Main render
  if (!currentUser) {
    return (
      <div className="w-full min-h-screen">
        <div className="flex flex-col items-center justify-center p-8 bg-white rounded-xl shadow-md">
          <h2 className="text-xl font-bold mb-4">Please log in to use FreeFlow</h2>
          <p>FreeFlow allows you to create synchronized timers with your ensemble.</p>
        </div>
      </div>
    );
  }
  
  if (subscriptionTier !== "pro" && subscriptionTier !== "basic") {
    return (
      <div className="w-full min-h-screen">
        <div className="flex flex-col items-center justify-center p-8 bg-white rounded-xl shadow-md">
          <h2 className="text-xl font-bold mb-4">FreeFlow requires a subscription</h2>
          <p className="mb-4">Upgrade to Basic or Pro to access synchronized timers with your ensemble.</p>
          <button 
            className="px-4 py-2 bg-accent-600 text-white rounded hover:bg-accent-500"
            onClick={() => window.location.href = '/upgrade'}
          >
            Upgrade Subscription
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-1 mx-auto bg-gray-250 min-h-screen w-full relative">
      
      {/* Screen Lock Overlay */}
      {isScreenLocked && (
        <div 
          className="fixed inset-0 z-[100] bg-black bg-opacity-80 flex flex-col items-center justify-center"
          onClick={handleScreenTap}
          style={{ 
            touchAction: 'manipulation',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none'
          }}
        >
          <div className="text-center text-white">
            {/* Lock Icon */}
            <div 
              className="mb-6 p-4 bg-white bg-opacity-20 rounded-full cursor-pointer hover:bg-opacity-30 transition-all duration-200"
              onClick={unlockScreen}
            >
              <Lock className="w-16 h-16 text-white" />
            </div>
            
            {/* Instructions */}
            <div className="space-y-2 max-w-sm mx-auto px-4">
              <h2 className="text-2xl font-bold mb-4">Screen Locked</h2>
              <p className="text-lg mb-2">To unlock:</p>
              <div className="space-y-1 text-sm opacity-90">
                <p>• Double-tap anywhere on screen</p>
                <p>• Press Ctrl-L (or ⌘-L on Mac)</p>
                <p>• Tap the lock icon above</p>
              </div>
            </div>
            
            {/* Additional info for mobile */}
            <div className="mt-8 text-xs opacity-75 max-w-xs mx-auto">
              <p>Screen lock prevents accidental taps while keeping the timer visible</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Global Timer Notification Overlay for Participants */}
      {showGlobalTimerNotification && activeSession && activeSession.creatorId !== currentUser.uid && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-500 bg-opacity-90 text-white px-4 py-3 text-center shadow-lg">
          <div className="flex items-center justify-center space-x-3">
            <div className="flex items-center">
              {showGlobalTimerNotification.type === 'reset' ? (
                <RotateCcw className="w-5 h-5 mr-2" />
              ) : (
                <Square className="w-5 h-5 mr-2" />
              )}
              <span className="font-medium">
                {showGlobalTimerNotification.message}
              </span>
            </div>
            <button
              onClick={() => resetTimer(false)}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-1 rounded text-sm font-medium transition-colors"
            >
              Reset My Timer
            </button>
            <button
              onClick={() => setShowGlobalTimerNotification(null)}
              className="text-white hover:text-gray-200 ml-2"
              title="Dismiss notification"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <ErrorMessage />
      
      {!activeSession ? (
        // Sessions list view
        <div>
          {/* Logo and description  */}
          
            <div className="text-center mb-5">
              <img
                src={flowFrameLogo}
                alt="FlowFrame Logo"
                className="inline-block w-48"
              />
            </div>
            <div className="text-center mb-6">
              <h2 className="text-2 mb-4 text-center">
                Empowering Confident Artistry
              </h2>
          
              <h3 className="text-lg text-gray-800">
                FreeFlow: Organize open, time-structured music
              </h3>
              <p className="text-gray-600 text-sm max-w-md mx-auto">
                Synchronize precise timings across musicians, facilitating music structured by timings rather than by rhythmic patterns.
              </p>
            </div>
         
          
          {/* Start of white content area */}
          <div className="bg-white p-6 rounded-xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">FreeFlow Sessions</h2>
              <div className="flex space-x-2">
                {pendingInvitations.length > 0 && (
                  <button
                    onClick={() => setShowInvitations(!showInvitations)}
                    className="flex items-center px-3 py-1 bg-blue-500 text-white rounded"
                  >
                    Invitations
                    <span className="ml-2 bg-white text-blue-500 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                      {pendingInvitations.length}
                    </span>
                  </button>
                )}
                <button 
                  onClick={() => setShowCreateSession(true)}
                  className="px-3 py-1 bg-accent-600 text-white rounded flex items-center"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  New Session
                </button>
              </div>
            </div>
          
            {/* Pending invitations */}
            {showInvitations && pendingInvitations.length > 0 && (
              <div className="mb-6">
                <h3 className="font-bold mb-2">Pending Invitations</h3>
                <div className="space-y-3">
                  {pendingInvitations.map(invitation => (
                    <div key={invitation.id} className="p-4 border-2 border-blue-200 rounded-lg bg-blue-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">{invitation.sessionName}</h4>
                          <p className="text-sm text-gray-600">
                            From: {invitation.creatorName || invitation.creatorEmail}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Sent {formatDate(invitation.createdAt)}
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleInvitation(invitation.id, true)}
                            className="px-2 py-1 bg-green-500 text-white rounded flex items-center"
                            title="Accept invitation"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Accept
                          </button>
                          <button
                            onClick={() => handleInvitation(invitation.id, false)}
                            className="px-2 py-1 bg-red-500 text-white rounded flex items-center"
                            title="Decline invitation"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Decline
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Sessions list */}
            {sessions.length === 0 ? (
              <p className="text-gray-500">You don't have any FreeFlow sessions yet.</p>
            ) : (
              <div className="space-y-3">
                {sessions.map(session => (
                  <div 
                    key={session.id}
                    className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <div className="flex justify-between items-center">
                      <h3 
                        className="font-medium flex-grow"
                        onClick={() => setActiveSession(session)}
                      >
                        {session.name}
                      </h3>
                      <div className="flex items-center">
                        <span className="text-sm text-gray-500 mr-3">
                          {session.creatorId === currentUser.uid ? "Owner" : "Participant"}
                        </span>
                        {session.creatorId === currentUser.uid && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSessionToDelete(session);
                              setShowDeleteConfirmation(true);
                            }}
                            className="text-red-500 hover:text-red-700 p-1"
                            title="Delete session"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div 
                      className="text-sm text-gray-500 mt-1"
                      onClick={() => setActiveSession(session)}
                    >
                      Created {formatDate(session.createdAt)}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Create session modal */}
            {showCreateSession && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg w-full max-w-md">
                  <h3 className="text-lg font-bold mb-4">Create New Session</h3>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Session Name</label>
                    <input
                      type="text"
                      value={newSessionName}
                      onChange={(e) => setNewSessionName(e.target.value)}
                      className="w-full p-2 border rounded"
                      placeholder="Title of the piece"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Count-in Duration</label>
                    <select
                      value={countInDuration}
                      onChange={(e) => setCountInDuration(Number(e.target.value))}
                      className="w-full p-2 border rounded"
                    >
                      <option value={0}>No count-in</option>
                      <option value={3}>3 seconds</option>
                      <option value={5}>5 seconds</option>
                      <option value={10}>10 seconds</option>
                    </select>
                  </div>
                  
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => setShowCreateSession(false)}
                      className="px-3 py-1 bg-gray-200 rounded"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={createNewSession}
                      className="px-3 py-1 bg-accent-600 text-white rounded"
                      disabled={!newSessionName.trim()}
                    >
                      Create
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Delete confirmation modal */}
            {showDeleteConfirmation && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg w-full max-w-md">
                  <h3 className="text-lg font-bold mb-4">Delete Session</h3>
                  
                  <p className="mb-4">
                    Are you sure you want to delete the session "{sessionToDelete?.name}"? 
                    This will remove all markers and invitations associated with this session.
                    This action cannot be undone.
                  </p>
                  
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => {
                        setShowDeleteConfirmation(false);
                        setSessionToDelete(null);
                      }}
                      className="px-3 py-1 bg-gray-200 rounded"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={deleteSession}
                      className="px-3 py-1 bg-red-500 text-white rounded"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        // Active session view
        <div className="bg-white p-6 rounded-xl">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold">{activeSession.name}</h2>
            <button 
              onClick={() => setActiveSession(null)}
              className="px-3 py-1 bg-gray-200 rounded flex items-center"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back to Sessions
            </button>
          </div>
          
          {/* Custom start time input - only show to creator */}
          {activeSession.creatorId === currentUser.uid && (
            <div className="flex flex-col gap-4 mb-4 p-3 border rounded-lg bg-gray-50">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">
                  Set Custom Start Time (MM:SS or seconds)
                </label>
                <div className="flex">
                  <input
                    type="text"
                    value={customStartTime}
                    onChange={(e) => setCustomStartTime(e.target.value)}
                    placeholder="e.g. 5:30 or 330"
                    className="flex-1 p-2 border rounded-l"
                  />
                  <button
                    onClick={setCustomTime}
                    className="px-3 py-2 bg-brand-500 text-white rounded-r"
                    disabled={!customStartTime.trim()}
                  >
                    Set Time
                  </button>
                </div>
              </div>

              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">
                  Count-in Duration
                </label>
                <div className="flex">
                  <select
                    value={activeSession.countInDuration || 0}
                    onChange={(e) => {
                      const newDuration = Number(e.target.value);
                      updateDoc(doc(db, "freeflows", activeSession.id), {
                        countInDuration: newDuration
                      }).catch(error => {
                        console.error("Error updating count-in duration:", error);
                        setError("Failed to update count-in duration. Please try again.");
                      });
                    }}
                    className="flex-1 p-2 border rounded"
                  >
                    <option value={0}>No count-in</option>
                    <option value={3}>3 seconds</option>
                    <option value={5}>5 seconds</option>
                    <option value={10}>10 seconds</option>
                  </select>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Count-in will display negative time before starting the timer
                </p>
              </div>
            </div>
          )}
          
          {/* Timer and countdowns display */}
          <div className="flex flex-col md:flex-row mb-4 gap-4">
            {/* Timer column */}
            <div className="flex-1 flex flex-col items-center">
              <div className="text-6xl font-mono font-bold mb-4 relative">
                {formatTime(elapsedTime)}
                {/* Show count-in indicator with more details */}
                {elapsedTime < 0 && (
                  <div className="text-sm font-normal text-blue-600 mt-1 text-center">
                    Count-in in progress: {Math.ceil(Math.abs(elapsedTime) / 1000)}s remaining
                  </div>
                )}
              </div>
              
              {/* Circle timer for next upcoming marker - now positioned below the timer */}
              {timerRunning && countdowns.length > 0 && (() => {
                // Find the current marker we should display
                // Look for a marker that is either:
                // 1) Completed within the last second and no other marker is more urgent
                // 2) The next upcoming marker
                
                let currentMarker = null;
                const now = Date.now();
                
                // First, check for recently completed markers (within last second)
                const recentlyCompletedMarker = countdowns
                  .filter(m => m.countdown >= -1000 && m.countdown <= 0)
                  .sort((a, b) => b.countdown - a.countdown)[0]; // Sort by most recently completed
                
                // Find the next upcoming marker that's in countdown phase
                const nextUpcomingMarkers = countdowns
                  .filter(m => m.countdown > 0)
                  .sort((a, b) => a.countdown - b.countdown);
                
                // Determine which marker to display
                if (recentlyCompletedMarker) {
                  // Show recently completed marker (will be green)
                  currentMarker = recentlyCompletedMarker;
                } else if (nextUpcomingMarkers.length > 0) {
                  // Show the next upcoming marker
                  currentMarker = nextUpcomingMarkers[0];
                }
                
                if (!currentMarker) return null;
                
                const nextMarker = currentMarker;
                const hasCountdown = nextMarker.countdownDuration > 0;
                const countdownMs = (hasCountdown ? nextMarker.countdownDuration : 10) * 1000;
                
                // Only show within countdown period or 1s after
                if (nextMarker.countdown <= countdownMs - 1000 && nextMarker.countdown >= -1000) {
                  // Calculate progress (0 to 1)
                  const progress = 1 - (Math.max(0, nextMarker.countdown) / countdownMs);
                  
                  // Size and positioning constants
                  const size = 160;
                  const center = size / 2;
                  const radius = 65;
                  
                  // Calculate chunks (seconds)
                  const totalSeconds = hasCountdown ? nextMarker.countdownDuration : 10;
                  const secondsLeft = Math.max(0, Math.floor(nextMarker.countdown / 1000) + 1);
                  const secondsElapsed = totalSeconds - secondsLeft;
                  
                  // Green only when fully completed
                  const isCompleted = nextMarker.countdown <= 0;
                  
                  return (
                    <div className="mb-4 relative" style={{ width: `${size}px`, height: `${size}px` }}>
                      {/* SVG circle timer */}
                      <svg 
                        width={size} 
                        height={size} 
                        viewBox={`0 0 ${size} ${size}`}
                      >
                        {/* Background circle */}
                        <circle 
                          cx={center} 
                          cy={center} 
                          r={radius} 
                          fill="transparent"
                          stroke="#f0f0f0"
                          strokeWidth="8"
                        />
                        
                        {/* Progress ring - fills in chunks */}
                        {Array.from({ length: totalSeconds }).map((_, i) => {
                          const segmentProgress = (i + 1) / totalSeconds;
                          // If complete, show all segments. Otherwise, show based on progress
                          const isActive = isCompleted || segmentProgress <= progress;
                          
                          if (!isActive) return null;
                          
                          const angleStart = -90; // Start at top (12 o'clock position)
                          const arcSize = 360 / totalSeconds;
                          const angleEnd = angleStart + (i + 1) * arcSize;
                          const angleStart2 = angleStart + i * arcSize;
                          
                          // SVG arc path calculation
                          const startX = center + radius * Math.cos(angleStart2 * Math.PI / 180);
                          const startY = center + radius * Math.sin(angleStart2 * Math.PI / 180);
                          const endX = center + radius * Math.cos(angleEnd * Math.PI / 180);
                          const endY = center + radius * Math.sin(angleEnd * Math.PI / 180);
                          
                          // Arc flags (large arc and sweep)
                          const largeArc = arcSize > 180 ? 1 : 0;
                          
                          return (
                            <path
                              key={i}
                              d={`M ${center} ${center} L ${startX} ${startY} A ${radius} ${radius} 0 ${largeArc} 1 ${endX} ${endY} Z`}
                              fill={isCompleted ? "#10B981" : "#B71E4B"}
                              fillOpacity="0.2"
                            />
                          );
                        })}
                        
                        {/* Second markers */}
                        {Array.from({ length: totalSeconds }).map((_, i) => {
                          const angle = (i / totalSeconds) * 360 - 90; // -90 to start at top
                          const isElapsed = i < secondsElapsed;
                          const isCurrentSecond = i === secondsElapsed - 1;
                          const dotRadius = 3;
                          
                          const x = center + (radius + 10) * Math.cos(angle * Math.PI / 180);
                          const y = center + (radius + 10) * Math.sin(angle * Math.PI / 180);
                          
                          return (
                            <circle
                              key={i}
                              cx={x}
                              cy={y}
                              r={dotRadius}
                              fill={
                                isElapsed 
                                ? isCompleted
                                  ? "#10B981" // green-500 
                                  : "#B71E4B"
                                : isCurrentSecond
                                  ? "#DD7896" // lighter red
                                  : "#D1D5DB" // gray-300
                              }
                            />
                          );
                        })}
                      </svg>
                      
                      {/* Label for the marker */}
                      <div className="absolute -top-6 w-full text-xs font-normal text-center">
                        <span className={isCompleted ? "text-green-600" : "text-accent-600"}>
                          {isCompleted ? `${nextMarker.name}` : `Next: ${nextMarker.name} in ${formatTime(nextMarker.countdown)}`}
                        </span>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
              
              {activeSession.creatorId === currentUser.uid ? (
                <>
                  <div className="flex space-x-4 mb-2">
                    {timerRunning ? (
                      <button
                        onClick={stopTimer}
                        disabled={isControlsLocked}
                        className={`px-4 py-2 rounded flex items-center ${
                          isControlsLocked 
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                            : 'bg-red-500 text-white hover:bg-red-600'
                        }`}
                        title={isControlsLocked ? "Controls are locked - unlock to use" : "Stop timer"}
                      >
                        <Square className="w-4 h-4 mr-2" />
                        Stop
                        {isControlsLocked && <Lock className="w-3 h-3 ml-2" />}
                      </button>
                    ) : (
                      <button
                        onClick={() => startTimer(activeSession)}
                        className="px-4 py-2 bg-brand-500 text-white rounded flex items-center hover:bg-brand-600"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Start
                        {activeSession.countInDuration > 0 && !timerRunning && elapsedTime === 0 && 
                         ` (${activeSession.countInDuration}s count-in)`}
                      </button>
                    )}
                    
                    {/* Reset button */}
                    <button
                      onClick={resetTimer}
                      disabled={isControlsLocked}
                      className={`px-4 py-2 rounded flex items-center ${
                        isControlsLocked 
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                          : 'bg-gray-500 text-white hover:bg-gray-600'
                      }`}
                      title={isControlsLocked ? "Controls are locked - unlock to use" : "Reset timer"}
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Reset
                      {isControlsLocked && <Lock className="w-3 h-3 ml-2" />}
                    </button>
                  </div>
                  
                  {/* Controls and Screen lock toggles for creators */}
                  <div className="flex justify-center space-x-3 mb-4">
                    <button
                      onClick={toggleControlsLock}
                      className={`px-3 py-1 text-sm rounded flex items-center ${
                        isControlsLocked 
                          ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      title={isControlsLocked ? "Unlock stop/reset controls" : "Lock stop/reset controls to prevent accidental clicks"}
                    >
                      {isControlsLocked ? (
                        <>
                          <Lock className="w-3 h-3 mr-1" />
                          Controls Locked
                        </>
                      ) : (
                        <>
                          <Unlock className="w-3 h-3 mr-1" />
                          Lock Controls
                        </>
                      )}
                    </button>
                    
                      <button
                      onClick={toggleScreenLock}
                        className={`px-3 py-1 text-sm rounded flex items-center ${
                        isScreenLocked 
                          ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      title={isScreenLocked ? "Screen is locked - Ctrl-L to unlock" : "Lock screen to prevent accidental taps"}
                    >
                      {isScreenLocked ? (
                        <>
                          <Lock className="w-3 h-3 mr-1" />
                          Screen Locked
                        </>
                      ) : (
                        <>
                          <Unlock className="w-3 h-3 mr-1" />
                          Lock Screen
                        </>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                // Participant controls section
                <>
                  <div className="mb-2 text-center">
                    <div className="mb-2 text-gray-500">
                      {timerRunning ? (
                        <span className="px-4 py-2 bg-gray-100 rounded inline-block">Timer running</span>
                      ) : (
                        <span className="px-4 py-2 bg-gray-100 rounded inline-block">Timer paused</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-3">Timer is controlled by the session creator</p>
                    
                    {/* Main resync button - prominently displayed */}
                    <div className="mb-3">
                      <button
                        onClick={resyncWithCreator}
                        className="px-6 py-3 bg-green-500 text-white rounded-lg flex items-center justify-center mx-auto hover:bg-green-600 font-medium"
                        title="Sync your timer with the creator's timer"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" 
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                          <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
                          <path d="M21 3v5h-5"></path>
                          <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
                          <path d="M3 21v-5h5"></path>
                        </svg>
                        Resync with Creator
                      </button>
                      <p className="text-xs text-gray-500 mt-1">
                        Click if your timer seems out of sync
                      </p>
                    </div>
                    
                    {/* Backup controls for participants */}
                    <div className="flex space-x-4 justify-center mb-2">
                      {timerRunning ? (
                        <button
                          onClick={() => stopTimer(false)}
                          disabled={isControlsLocked}
                          className={`px-4 py-2 rounded flex items-center ${
                            isControlsLocked 
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                              : 'bg-red-400 text-white hover:bg-red-500'
                            }`}
                          title={isControlsLocked ? "Controls are locked - unlock to use" : "Stop your local timer if out of sync"}
                        >
                          <Square className="w-4 h-4 mr-2" />
                          Stop (Local)
                          {isControlsLocked && <Lock className="w-3 h-3 ml-2" />}
                        </button>
                      ) : (
                        <button
                          onClick={() => startLocalTimer(activeSession)}  // Changed from startTimer to startLocalTimer
                          className="px-4 py-2 bg-blue-400 text-white rounded flex items-center hover:bg-blue-500"
                          title="Restart your local timer if out of sync"
                        >
                          <Play className="w-4 h-4 mr-2" />
                          Start (Local)
                        </button>
                      )}
                      
                      {/* Local reset button */}
                      <button
                        onClick={() => resetTimer(false)}
                        disabled={isControlsLocked}
                        className={`px-4 py-2 rounded flex items-center ${
                          isControlsLocked 
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                            : 'bg-gray-400 text-white hover:bg-gray-500'
                        }`}
                        title={isControlsLocked ? "Controls are locked - unlock to use" : "Reset your local timer if out of sync"}
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Reset (Local)
                        {isControlsLocked && <Lock className="w-3 h-3 ml-2" />}
                      </button>
                    </div>
                    
                    {/* Controls and Screen lock toggles for participants */}
                    <div className="flex justify-center space-x-3">
                      <button
                        onClick={toggleControlsLock}
                        className={`px-3 py-1 text-sm rounded flex items-center ${
                          isControlsLocked 
                            ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        title={isControlsLocked ? "Unlock stop/reset controls" : "Lock stop/reset controls to prevent accidental clicks"}
                      >
                        {isControlsLocked ? (
                          <>
                            <Lock className="w-3 h-3 mr-1" />
                            Controls Locked
                          </>
                        ) : (
                          <>
                            <Unlock className="w-3 h-3 mr-1" />
                            Lock Controls
                          </>
                        )}
                      </button>
                      
                      <button
                        onClick={toggleScreenLock}
                        className={`px-3 py-1 text-sm rounded flex items-center ${
                          isScreenLocked 
                            ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        title={isScreenLocked ? "Screen is locked - Ctrl-L to unlock" : "Lock screen to prevent accidental taps"}
                      >
                        {isScreenLocked ? (
                          <>
                            <Lock className="w-3 h-3 mr-1" />
                            Screen Locked
                          </>
                        ) : (
                          <>
                            <Unlock className="w-3 h-3 mr-1" />
                            Lock Screen
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </>
              )}
              
              {/* Wake Lock status indicator */}
              {wakeLockSupported && (
                <div className="text-xs mt-2 text-center flex items-center justify-center">
                  <div className={`w-2 h-2 rounded-full mr-1 ${
                    wakeLockStatus === "active" ? 'bg-green-500' : 
                    wakeLockStatus === "attempting" ? 'bg-yellow-500' :
                    wakeLockStatus === "error" ? 'bg-red-500' : 'bg-gray-300'
                  }`}></div>
                  <span className={
                    wakeLockStatus === "active" ? 'text-green-600' : 
                    wakeLockStatus === "attempting" ? 'text-yellow-600' :
                    wakeLockStatus === "error" ? 'text-red-600' : 'text-gray-500'
                  }>
                    {wakeLockStatus === "active" ? 'Screen will stay awake while timer is running' : 
                     wakeLockStatus === "attempting" ? 'Activating screen wake lock...' :
                     wakeLockStatus === "error" ? 'Failed to keep screen awake' :
                     'Screen may time out (tap to wake)'}
                  </span>
                </div>
              )}
            </div>
            
            {/* Upcoming markers column */}
            <div className="flex-1">
              {countdowns.length > 0 ? (
                <div className="h-full p-4 border-2 border-yellow-200 rounded-lg bg-yellow-50">
                  <h3 className="font-bold mb-4 flex items-center">
                    <Clock className="w-4 h-4 mr-2" />
                    Next Upcoming Markers
                  </h3>
                  <div className="space-y-4">
                    {countdowns.slice(0, 2).map(marker => {
                      const hasCountdown = marker.countdownDuration > 0;
                      const countdownMs = hasCountdown ? marker.countdownDuration * 1000 : 0;
                      const isWithinCountdown = marker.countdown <= countdownMs;
                      
                      return (
                        <div key={marker.id} className="p-3 bg-white rounded-lg shadow-sm">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium text-lg">{marker.name}</span>
                            <span className="font-mono font-bold text-accent-600">
                              in {formatTime(marker.countdown)}
                            </span>
                          </div>
                          
                          {/* Only show countdown UI if within the countdown period */}
                          {isWithinCountdown ? (
                            <>
                              <div className="w-full bg-gray-200 rounded-md h-4 overflow-hidden mb-1">
                                <div 
                                  className="h-4 bg-accent-600 rounded-l flex items-center justify-end pr-2"
                                  style={{ 
                                    width: `${Math.max(5, 100 - (marker.countdown / countdownMs * 100))}%` 
                                  }}
                                >
                                  {marker.countdown < 10000 && (
                                    <span className="text-white text-xs font-bold">
                                      {Math.floor(marker.countdown / 1000) + 1}s
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              {/* Discrete second indicators */}
                              <div className="w-full flex">
                                {Array.from({ length: Math.min(60, hasCountdown ? marker.countdownDuration : 10) }).map((_, i) => {
                                  // Calculate if this second has elapsed relative to the countdown
                                  const totalSeconds = hasCountdown ? marker.countdownDuration : 10;
                                  const secondsLeft = Math.floor(marker.countdown / 1000) + 1;
                                  const secondsElapsed = totalSeconds - secondsLeft;
                                  const isElapsed = i < secondsElapsed;
                                  const isCurrentSecond = i === secondsElapsed - 1;
                                  
                                  return (
                                    <div 
                                      key={i} 
                                      className={`flex-1 border-r last:border-r-0 ${
                                        isElapsed 
                                          ? 'bg-gray-300' 
                                          : isCurrentSecond
                                            ? 'bg-accent-600 bg-opacity-30'
                                            : ''
                                      }`}
                                      style={{ height: '2px' }}
                                    ></div>
                                  );
                                })}
                              </div>
                            </>
                          ) : (
                            <div className="w-full py-2 text-sm text-gray-500 italic text-center">
                              {hasCountdown 
                                ? `Countdown starts in ${formatTime(marker.countdown - countdownMs)}`
                                : "No countdown set for this marker"
                              }
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {countdowns.length > 2 && (
                      <div className="text-center text-gray-500 text-sm">
                        {countdowns.length - 2} more marker{countdowns.length - 2 > 1 ? 's' : ''} upcoming
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center p-4 border-2 border-gray-200 rounded-lg bg-gray-50">
                  <p className="text-gray-500 text-center">
                    No upcoming markers selected.<br/>
                    <span className="text-sm">Select markers from the list below to see them here.</span>
                  </p>
                </div>
              )}
            </div>
          </div>
          
          
          {/* Markers section - update to show personalized names and include participant edit buttons */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-bold">Time Markers</h3>
              {activeSession.creatorId === currentUser.uid && (
                <button 
                  onClick={() => setShowAddMarker(true)}
                  className="px-2 py-1 text-sm bg-accent-600 text-white rounded flex items-center"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Marker
                </button>
              )}
            </div>
            
            {markers.length === 0 ? (
              <p className="text-gray-500 text-sm">No markers added yet.</p>
            ) : (
              <div className="space-y-2">
                {markers
                  .sort((a, b) => a.timeMs - b.timeMs)
                  .map(marker => {
                    const personalizedMarker = getPersonalizedMarker(marker);
                    const isPersonalized = personalizedMarkers[marker.id];
                    
                    return (
                      <div 
                        key={marker.id}
                        className={`p-3 border-2 rounded-lg flex justify-between items-center ${
                          selectedMarkers.includes(marker.id) 
                            ? 'border-blue-300 bg-blue-50' 
                            : 'border-gray-200'
                        }`}
                        onClick={() => toggleMarkerSelection(marker.id)}
                      >
                        <div>
                          <div className="font-medium flex items-center">
                            {personalizedMarker.name}
                            {isPersonalized && (
                              <span className="ml-2 text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">
                                Personalized
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatTime(personalizedMarker.timeMs)}
                            {personalizedMarker.countdownDuration && (
                              <span className="ml-2 text-accent-600">
                                ({personalizedMarker.countdownDuration}s countdown)
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center">
                          {selectedMarkers.includes(marker.id) && (
                            <span className="text-blue-500 mr-2 text-sm">
                              Selected
                            </span>
                          )}
                          
                          {/* Vibration toggle button */}
                          <button
                            onClick={(e) => toggleMarkerVibration(e, marker.id)}
                            className={`p-1 rounded-full mr-1 ${
                              markerVibrations[marker.id] 
                                ? 'bg-yellow-100 text-yellow-600' 
                                : 'bg-gray-100 text-gray-400'
                            }`}
                            title={`${markerVibrations[marker.id] ? 'Disable' : 'Enable'} vibration when marker is reached`}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" 
                              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                              <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                            </svg>
                          </button>
                          
                          {/* Participant personalization button */}
                          {activeSession.creatorId !== currentUser.uid && (
                            <button 
                              onClick={(e) => openPersonalizeMarker(marker, e)}
                              className="text-purple-500 hover:text-purple-700 p-1 mr-1"
                              title="Personalize marker name and countdown (only affects your view)"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          )}
                          
                          {/* Reset personalization button */}
                          {activeSession.creatorId !== currentUser.uid && isPersonalized && (
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                resetPersonalizedMarker(marker.id);
                              }}
                              className="text-gray-500 hover:text-gray-700 p-1 mr-1"
                              title="Reset to original marker settings"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                          )}
                          
                          {/* Creator edit/delete buttons */}
                          {activeSession.creatorId === currentUser.uid && (
                            <>
                              <button 
                                onClick={(e) => openEditMarker(marker, e)}
                                className="text-blue-500 hover:text-blue-700 p-1"
                                title="Edit marker"
                              >
                                <Edit className="w-5 h-5" />
                              </button>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteMarker(marker.id);
                                }}
                                className="text-red-500 hover:text-red-700 p-1"
                                title="Delete marker"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
          
          {/* Invite users (only for creator) */}
          {activeSession.creatorId === currentUser.uid && (
            <div className="border-t pt-4">
              <h3 className="font-bold mb-2">Invite Participants</h3>
              <div className="flex space-x-2">
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="Email address"
                  className="flex-1 p-2 border rounded"
                />
                <button
                  onClick={inviteUser}
                  className="px-3 py-2 bg-brand-500 text-white rounded flex items-center"
                  disabled={!inviteEmail.trim()}
                >
                  <UserPlus className="w-4 h-4 mr-1" />
                  Invite
                </button>
              </div>
            </div>
          )}
          
          {/* Participants list */}
          <div className="border-t pt-4 mt-4">
            <h3 className="font-bold mb-2">Participants</h3>
            <div className="text-sm text-gray-600">
              <div className="mb-1">
                {activeSession.creatorId === currentUser.uid ? (
                  <span className="font-medium">You (Owner)</span>
                ) : (
                  <span>Owner</span>
                )}
              </div>
              {activeSession.participants && activeSession.participants.length > 1 && (
                <div className="text-gray-500">
                  {activeSession.participants.length - 1} other participant(s)
                </div>
              )}
            </div>
          </div>
          
          {/* Add marker modal */}
          {showAddMarker && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg w-full max-w-md">
                <h3 className="text-lg font-bold mb-4">Add Time Marker</h3>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Marker Name</label>
                  <input
                    type="text"
                    value={newMarkerName}
                    onChange={(e) => setNewMarkerName(e.target.value)}
                    className="w-full p-2 border rounded"
                    placeholder="e.g. Start second movement"
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Time (MM:SS or seconds)</label>
                  <input
                    type="text"
                    value={newMarkerTime}
                    onChange={(e) => setNewMarkerTime(e.target.value)}
                    className="w-full p-2 border rounded"
                    placeholder="e.g. 2:30 or 150"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter time in MM:SS format (e.g. 2:30) or total seconds (e.g. 150)
                  </p>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Countdown Duration (seconds)</label>
                  <input
                    type="number"
                    value={newMarkerCountdown}
                    onChange={(e) => setNewMarkerCountdown(e.target.value)}
                    className="w-full p-2 border rounded"
                    placeholder="e.g. 10"
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Optional: Set a countdown that will appear before this marker
                  </p>
                </div>
                
                {/* Vibration toggle for Add Marker */}
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newMarkerVibration}
                      onChange={(e) => setNewMarkerVibration(e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm">
                      Vibrate when marker is reached 
                      <span className="text-xs text-gray-500 ml-1">(mobile devices only)</span>
                    </span>
                  </label>
                </div>
                
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => setShowAddMarker(false)}
                    className="px-3 py-1 bg-gray-200 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={addMarker}
                    className="px-3 py-1 bg-accent-600 text-white rounded"
                    disabled={!newMarkerName.trim() || !newMarkerTime}
                  >
                    Add Marker
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Edit marker modal */}
          {showEditMarker && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg w-full max-w-md">
                <h3 className="text-lg font-bold mb-4">Edit Time Marker</h3>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Marker Name</label>
                  <input
                    type="text"
                    value={editMarkerName}
                    onChange={(e) => setEditMarkerName(e.target.value)}
                    className="w-full p-2 border rounded"
                    placeholder="e.g. Start second movement"
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Time (MM:SS or seconds)</label>
                  <input
                    type="text"
                    value={editMarkerTime}
                    onChange={(e) => setEditMarkerTime(e.target.value)}
                    className="w-full p-2 border rounded"
                    placeholder="e.g. 2:30 or 150"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter time in MM:SS format (e.g. 2:30) or total seconds (e.g. 150)
                  </p>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Countdown Duration (seconds)</label>
                  <input
                    type="number"
                    value={editMarkerCountdown}
                    onChange={(e) => setEditMarkerCountdown(e.target.value)}
                    className="w-full p-2 border rounded"
                    placeholder="e.g. 10"
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Optional: Set a countdown that will appear before this marker
                  </p>
                </div>
                
                {/* Vibration toggle for Edit Marker */}
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editMarkerVibration}
                      onChange={(e) => setEditMarkerVibration(e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm">
                      Vibrate when marker is reached 
                      <span className="text-xs text-gray-500 ml-1">(mobile devices only)</span>
                    </span>
                  </label>
                </div>
                
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => setShowEditMarker(false)}
                    className="px-3 py-1 bg-gray-200 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveEditedMarker}
                    className="px-3 py-1 bg-accent-600 text-white rounded"
                    disabled={!editMarkerName.trim() || !editMarkerTime}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Personalize marker modal for participants */}
          {showPersonalizeMarker && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg w-full max-w-md">
                <h3 className="text-lg font-bold mb-4">Personalize Marker (Your View Only)</h3>
                
                <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded">
                  <p className="text-sm text-purple-700">
                    <strong>Note:</strong> These changes only affect how this marker appears to you. 
                    Other participants will see the original marker settings. You cannot change the timing of the marker.
                  </p>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">
                    Marker Name
                    <span className="text-xs text-gray-500 ml-1">(personalized)</span>
                  </label>
                  <input
                    type="text"
                    value={personalizeMarkerName}
                    onChange={(e) => setPersonalizeMarkerName(e.target.value)}
                    className="w-full p-2 border rounded"
                    placeholder="e.g. My cue for entrance"
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">
                    Countdown Duration (seconds)
                    <span className="text-xs text-gray-500 ml-1">(personalized)</span>
                  </label>
                  <input
                    type="number"
                    value={personalizeMarkerCountdown}
                    onChange={(e) => setPersonalizeMarkerCountdown(e.target.value)}
                    className="w-full p-2 border rounded"
                    placeholder="e.g. 10"
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Set your preferred countdown duration for this marker
                  </p>
                </div>
                
                <div className="mb-4 p-2 bg-gray-50 rounded">
                  <p className="text-sm text-gray-600">
                    <strong>Original:</strong> {personalizingMarker?.name} at {formatTime(personalizingMarker?.timeMs)} 
                    {personalizingMarker?.countdownDuration && ` (${personalizingMarker.countdownDuration}s countdown)`}
                  </p>
                </div>
                
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => setShowPersonalizeMarker(false)}
                    className="px-3 py-1 bg-gray-200 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={savePersonalizedMarker}
                    className="px-3 py-1 bg-purple-500 text-white rounded"
                    disabled={!personalizeMarkerName.trim()}
                  >
                    Save Personalization
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    
    </div>
  );
}

// Add this export to make the component's invitations count accessible to parent components
export function FreeFlowInvitationsBadge({ currentUser }) {
  const [invitationsCount, setInvitationsCount] = useState(0);
  
  useEffect(() => {
    if (!currentUser) {
      setInvitationsCount(0);
      return;
    }
    
    try {
      const q = query(
        collection(db, "invitations"),
        where("recipientEmail", "==", currentUser.email),
        where("status", "==", "pending")
      );
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        setInvitationsCount(snapshot.docs.length);
      }, (error) => {
        console.error("Error getting invitations count:", error);
      });
      
      return () => unsubscribe();
    } catch (error) {
      console.error("Error setting up invitations badge listener:", error);
      return () => {};
    }
  }, [currentUser]);
  
  if (invitationsCount === 0) return null;
  
  return (
    <span className="bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs absolute -top-1 -right-1">
      {invitationsCount}
    </span>
  );
}



